import { useCallback, useRef, useState } from 'react';
import { formatWibJam, type TimelineSegment } from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Bilah kendali **di dalam kotak video** halaman Playback (§G10.3).
 *
 * Alasannya lahir: begitu videonya dijadikan layar penuh, seluruh kendali
 * halaman ini — kelompok transport, timeline, tombol unduh — tertinggal di
 * belakang layar penuh dan tidak bisa disentuh sama sekali. Yang tersisa cuma
 * papan ketik (`§G10.1`), dan itu berarti layar penuh praktis mati bagi pemakai
 * tetikus, dan **benar-benar** mati bagi layar sentuh: tidak ada satu pun cara
 * menjeda atau memindahkan posisi tanpa keluar dulu dari layar penuh.
 *
 * Karena itu ia tidak dibuat sebagai "bilah khusus layar penuh". Bilah yang
 * cuma ada di layar penuh adalah kendali yang harus ditemukan dua kali; yang
 * dipasang di sini pemutar biasa — sama seperti video embed mana pun — yang
 * kebetulan ikut membesar saat layar penuh.
 *
 * **Sumbunya jam dinding, bukan detik di dalam berkas.** Satu putaran di
 * halaman ini dijahit dari banyak segmen 15 menit (`§D8.1`) dan `<video>` cuma
 * tahu segmen yang sedang dipegangnya; `currentTime`-nya karena itu tidak
 * berarti apa-apa sebagai "posisi". Yang dipakai di sini rentang yang sedang
 * termuat, persis batas yang ditegakkan `seek()` di halaman.
 *
 * **Bolong rekaman tetap kelihatan sebagai bolong** (`§D8.1`): jalurnya hanya
 * terisi di tempat yang memang ada rekamannya. Bilah penuh dari ujung ke ujung
 * akan menjanjikan rekaman yang tidak pernah ada — kesalahan yang justru paling
 * mahal di halaman ini, karena orang menyimpulkan "tidak ada kejadian" dari
 * gambar yang sebenarnya tidak pernah terekam.
 */

interface Props {
  /** Rentang yang sedang termuat — sama dengan batas yang dijaga `seek()`. */
  fromMs: number;
  toMs: number;
  /** Posisi playhead, jam dinding. `null` = belum ada yang diputar. */
  positionMs: number | null;
  segments: TimelineSegment[];
  main: boolean;
  mundur: number;
  rate: number;
  /** Bilahnya sedang ditampilkan — lihat penyembunyian otomatis di pemanggil. */
  tampil: boolean;
  /** Sedang layar penuh: semuanya membesar, karena jaraknya ke mata bertambah. */
  besar: boolean;
  onSeek: (ms: number) => void;
  onPutar: () => void;
  onJeda: () => void;
  onMundur: () => void;
  onMaju: () => void;
  onNormal: () => void;
  /**
   * Tetikus masuk / keluar dari bilahnya. Selama ia di atas bilah, bilahnya
   * tidak boleh pergi — tangan yang sudah sampai di tombol jelas belum selesai.
   */
  onTahan: (di: boolean) => void;
  /**
   * Playhead mulai / selesai diseret.
   *
   * Bedanya dengan `onTahan`: yang itu soal tetikus yang kebetulan lewat, yang
   * ini soal jalur waktu yang sedang **dipegang**. Selama dipegang, bilahnya
   * tidak boleh tidur (tangan yang sedang menyeret jelas belum selesai) dan
   * pemanggil berhenti menimpa posisi playhead dengan posisi videonya sendiri.
   */
  onSeret: (sedang: boolean) => void;
  /**
   * Pindah ke potongan sebelum / sesudah (§G10.5).
   *
   * Pindah **berkas**, bukan geser sekian detik — lihat `lompatSegmen` di
   * halaman. Ia masuk ke bilah ini 8 September 2026, bersamaan dengan
   * dibuangnya sepasang panah besar yang dulu melayang di kiri-kanan gambar:
   * *"untuk 'clip berikutnya' gak usah ada tombol < > di layar, di tombol
   * control aja udah cukup."*
   *
   * Wajib ada **di sini**, bukan cuma di kelompok transport bawah gambar. Di
   * layar penuh dan di ponsel kelompok itu padam (§G10.3) dan bilah inilah
   * satu-satunya kendali yang bisa dijangkau; tanpa keduanya, membuang panah di
   * gambar berarti mencabut pindah-potongan dari layar sentuh sama sekali.
   */
  onKlipSebelum: () => void;
  onKlipSesudah: () => void;
}

export function PlayerBar({
  fromMs,
  toMs,
  positionMs,
  segments,
  main,
  mundur,
  rate,
  tampil,
  besar,
  onSeek,
  onPutar,
  onJeda,
  onMundur,
  onMaju,
  onNormal,
  onTahan,
  onSeret,
  onKlipSebelum,
  onKlipSesudah,
}: Props) {
  const { t } = useBahasa();
  const jalurRef = useRef<HTMLDivElement>(null);
  const seretRef = useRef(false);
  const [pratinjau, setPratinjau] = useState<number | null>(null);

  const rentang = Math.max(1, toMs - fromMs);
  /** Jam dinding → persen lebar jalur. Persen, bukan piksel: tidak ada lebar
      yang perlu diukur, jadi tidak ada ResizeObserver dan tidak ada satu render
      pun yang menunggu pengukuran itu. */
  const persen = useCallback(
    (ms: number) => Math.min(100, Math.max(0, ((ms - fromMs) / rentang) * 100)),
    [fromMs, rentang],
  );

  const msDari = useCallback(
    (clientX: number) => {
      const r = jalurRef.current?.getBoundingClientRect();
      if (!r) return fromMs;
      const p = Math.min(1, Math.max(0, (clientX - r.left) / Math.max(1, r.width)));
      return fromMs + p * rentang;
    },
    [fromMs, rentang],
  );

  const turun = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    seretRef.current = true;
    onSeret(true);
    onSeek(msDari(e.clientX));
  };

  const gerak = (e: React.PointerEvent) => {
    const ms = msDari(e.clientX);
    setPratinjau(ms);
    if (seretRef.current) onSeek(ms);
  };

  const naik = (e: React.PointerEvent) => {
    seretRef.current = false;
    onSeret(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const posPersen = positionMs === null ? 0 : persen(positionMs);
  const berjalan = main || mundur > 0;

  /** Blok rekaman di sepanjang jalur; yang tidak digambar adalah bolongnya. */
  const blok = (warna: string) =>
    segments.map((s) => {
      const a = new Date(s.startedAt).getTime();
      const b = new Date(s.endedAt).getTime();
      if (b < fromMs || a > toMs) return null;
      const kiri = persen(a);
      return (
        <span
          key={s.id}
          className={`absolute inset-y-0 ${warna}`}
          style={{ left: `${kiri}%`, width: `${Math.max(0.15, persen(b) - kiri)}%` }}
        />
      );
    });

  return (
    <div
      /* Ditandai `data-kendali` supaya gestur zoom di kotak gambar (§G10.6)
         melewatinya: menekan ⏵ dua kali cepat bukan ketuk ganda, dan menyeret
         jalur waktu bukan geseran gambar. */
      data-kendali=""
      /* Gradien, bukan bilah berlatar solid: yang di bawahnya gambar kamera, dan
         balok hitam setinggi 90px memotong bagian bawah adegan justru di tempat
         orang paling sering berdiri. */
      className={`absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/85 via-black/55 to-transparent text-white transition-opacity duration-200 motion-reduce:transition-none ${
        besar ? 'px-5 pb-4 pt-14' : 'px-3 pb-2 pt-10'
      } ${tampil ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      onPointerEnter={() => onTahan(true)}
      onPointerLeave={() => onTahan(false)}
    >
      {/* ── Jalur waktu ──────────────────────────────────────────────────── */}
      <div
        ref={jalurRef}
        role="slider"
        tabIndex={0}
        aria-label={t('Playback position')}
        aria-valuemin={fromMs}
        aria-valuemax={toMs}
        aria-valuenow={positionMs ?? fromMs}
        aria-valuetext={positionMs === null ? '—' : formatWibJam(positionMs)}
        onPointerDown={turun}
        onPointerMove={gerak}
        onPointerUp={naik}
        onPointerCancel={naik}
        onPointerLeave={() => setPratinjau(null)}
        /* `touch-none` supaya seretan jari sampai ke sini alih-alih menggulung
           halaman. Padding tegaknya yang jadi target sentuh: jalurnya sendiri
           6px, dan 6px tidak bisa dikenai jari (§G7 no.3). */
        className={`group relative w-full cursor-pointer touch-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white ${
          besar ? 'py-3' : 'py-2.5'
        }`}
      >
        <div className={`relative w-full rounded-full bg-white/25 ${besar ? 'h-2' : 'h-1.5'}`}>
          {/* Rekaman yang ada — dua lapis blok yang sama persis, yang kedua
              dipangkas di posisi playhead. Dipangkas dengan `clip-path`, bukan
              dengan pembungkus selebar sekian persen: pembungkus mengubah acuan
              persen anak-anaknya, jadi blok yang sama harus dihitung dua kali
              dengan dua rumus berbeda — dan dua rumus untuk satu gambar adalah
              dua tempat yang bisa berbeda. */}
          <span className="absolute inset-0 overflow-hidden rounded-full">{blok('bg-white/55')}</span>
          <span
            className="absolute inset-0 overflow-hidden rounded-full"
            style={{ clipPath: `inset(0 ${100 - posPersen}% 0 0)` }}
          >
            {blok('bg-accent')}
          </span>
        </div>

        {/* Pegangan. Mengecil saat tidak disentuh supaya tidak menutupi jalur,
            membesar begitu tangan mendekat — kebiasaan pemutar mana pun. */}
        {positionMs !== null && (
          <span
            className={`pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow ring-1 ring-black/30 transition-[height,width] duration-150 motion-reduce:transition-none ${
              besar ? 'h-3.5 w-3.5 group-hover:h-4 group-hover:w-4' : 'h-3 w-3 group-hover:h-3.5 group-hover:w-3.5'
            }`}
            style={{ left: `${posPersen}%` }}
          />
        )}

        {/* Jam di bawah telunjuk sebelum diklik. Di rentang satu jam, satu
            piksel ≈ empat detik: tanpa ini memindahkan playhead ke menit
            tertentu berarti menebak, lalu mengoreksi. */}
        {pratinjau !== null && (
          <span
            className="pointer-events-none absolute bottom-full mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-black/85 px-1.5 py-0.5 text-xs tabular-nums ring-1 ring-white/20"
            style={{ left: `${persen(pratinjau)}%` }}
          >
            {formatWibJam(pratinjau)}
          </span>
        )}
      </div>

      {/* ── Tombol ───────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1">
        {/* Hanya kalau memang ada yang dituju: tombol yang selalu ada lalu tidak
            melakukan apa-apa mengajari orang untuk berhenti memercayainya —
            alasan yang sama yang dulu dipakai panah di gambar. */}
        {segments.length > 1 && (
          <Tombol
            onClick={onKlipSebelum}
            title={t('Previous clip (PgUp)')}
            label={t('Previous clip')}
            aktif={false}
            besar={besar}
          >
            <path d="M20 5.5a.8.8 0 0 0-1.24-.66l-7 4.5a.8.8 0 0 0 0 1.32l7 4.5A.8.8 0 0 0 20 14.5z" />
            <rect x="4" y="4.6" width="2.6" height="10.8" rx="1" />
          </Tombol>
        )}

        <Tombol
          onClick={onMundur}
          title={t('Rewind (J)')}
          label={mundur > 0 ? t('Rewinding {n}×', { n: mundur }) : t('Rewind')}
          aktif={mundur > 0}
          besar={besar}
        >
          <path d="M11 5.5a.8.8 0 0 0-1.24-.66l-7 4.5a.8.8 0 0 0 0 1.32l7 4.5A.8.8 0 0 0 11 14.5z" />
          <path d="M21 5.5a.8.8 0 0 0-1.24-.66l-7 4.5a.8.8 0 0 0 0 1.32l7 4.5A.8.8 0 0 0 21 14.5z" />
        </Tombol>

        <Tombol
          onClick={berjalan ? onJeda : onPutar}
          title={berjalan ? t('Pause (Space)') : t('Play (Space)')}
          label={berjalan ? t('Pause') : t('Play')}
          aktif={false}
          besar={besar}
        >
          {berjalan ? (
            <>
              <rect x="6.5" y="5" width="3.6" height="14" rx="1" />
              <rect x="13.9" y="5" width="3.6" height="14" rx="1" />
            </>
          ) : (
            <path d="M9 5.14a1 1 0 0 1 1.53-.85l9.72 6.86a1 1 0 0 1 0 1.7l-9.72 6.86A1 1 0 0 1 9 18.86z" />
          )}
        </Tombol>

        <Tombol
          onClick={onMaju}
          title={t('Fast forward (L)')}
          label={rate > 1 ? t('Forward {n}×', { n: rate }) : t('Fast forward')}
          aktif={rate > 1}
          besar={besar}
        >
          <path d="M3 5.5a.8.8 0 0 1 1.24-.66l7 4.5a.8.8 0 0 1 0 1.32l-7 4.5A.8.8 0 0 1 3 14.5z" />
          <path d="M13 5.5a.8.8 0 0 1 1.24-.66l7 4.5a.8.8 0 0 1 0 1.32l-7 4.5A.8.8 0 0 1 13 14.5z" />
        </Tombol>

        {segments.length > 1 && (
          <Tombol
            onClick={onKlipSesudah}
            title={t('Next clip (PgDn)')}
            label={t('Next clip')}
            aktif={false}
            besar={besar}
          >
            <path d="M4 5.5a.8.8 0 0 1 1.24-.66l7 4.5a.8.8 0 0 1 0 1.32l-7 4.5A.8.8 0 0 1 4 14.5z" />
            <rect x="17.4" y="4.6" width="2.6" height="10.8" rx="1" />
          </Tombol>
        )}

        {/* Penunjuk laju yang sekaligus jalan pulang ke 1× (§G10.1) — aturan
            yang sama dengan kelompok transport di bawah gambar: tetap terpasang
            saat 1×, redup dan mati, supaya ia sudah ditemukan sebelum
            dibutuhkan. Di layar penuh ia satu-satunya jalan pulang yang bukan
            papan ketik. */}
        <button
          type="button"
          onClick={onNormal}
          disabled={rate === 1 && mundur === 0}
          title={
            rate === 1 && mundur === 0
              ? t('Playing at normal speed')
              : t('Back to 1× (K stops instead)')
          }
          aria-label={t('Back to normal speed')}
          className={`ml-0.5 flex items-center justify-center rounded-full px-2 text-sm font-semibold tabular-nums transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:text-white/45 ${
            besar ? 'h-11 min-w-11' : 'h-9 min-w-9'
          } ${rate === 1 && mundur === 0 ? '' : 'bg-accent text-accent-contrast'}`}
        >
          {mundur > 0 ? `−${mundur}×` : `${rate}×`}
        </button>

        {/* Jam dinding, bukan "01:12 / 60:00". Yang dicari orang di rekaman CCTV
            selalu disebut sebagai jam — "sekitar setengah tiga" — dan bukan
            sebagai detik ke sekian dari awal rentang. */}
        <span
          className={`ml-auto tabular-nums drop-shadow ${besar ? 'text-base' : 'text-sm'}`}
          aria-live="off"
        >
          {positionMs === null ? '—' : formatWibJam(positionMs)}
          <span className="text-white/60"> / {formatWibJam(toMs)}</span>
        </span>
      </div>
    </div>
  );
}

/** Satu tombol transport di dalam bilah. Bulat, gelap, target ≥44px (§G7 no.3). */
function Tombol({
  onClick,
  title,
  label,
  aktif,
  besar,
  children,
}: {
  onClick: () => void;
  title: string;
  label: string;
  aktif: boolean;
  besar: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={label}
      aria-pressed={aktif}
      className={`flex items-center justify-center rounded-full transition-[background-color,scale] duration-150 hover:bg-white/20 active:scale-[0.94] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
        besar ? 'h-12 w-12' : 'h-10 w-10'
      } ${aktif ? 'bg-white/20' : ''}`}
    >
      <svg
        viewBox="0 0 24 24"
        className={besar ? 'h-6 w-6' : 'h-5 w-5'}
        fill="currentColor"
        aria-hidden="true"
      >
        {children}
      </svg>
    </button>
  );
}
